<template>
  <el-popover placement="top" :show-arrow="false" :width="320" :disabled="isDisabled">
    <template #reference>
      <slot>
        <!-- Иконка идёт по потоку текста, а не отдельной колонкой flex: иначе
             у даты, которой разрешили перенос, она отрывается от последней
             строки и уезжает вбок. Пробела перед ней нет намеренно — так она
             не отрывается от последнего слова. -->
        <span class="inline-block whitespace-nowrap" :class="valueClass"
          >{{ isReverse ? localDate : formattedDate
          }}<ITimeForward
            v-if="!isDisabled"
            class="relative -top-px ml-1 inline-block size-3 min-w-3 align-middle text-primary"
        /></span>
      </slot>
    </template>
    <div class="text-center">
      {{ isReverse ? "По Московскому времени" : "По локальному времени" }}:
      <b>{{ isReverse ? formattedDate : localDate }}</b>
    </div>
  </el-popover>
</template>

<script setup lang="ts">
  import dayjs from "dayjs";
  import ITimeForward from "~/components/adv/orders/icons/ITimeForward.vue";

  const props = withDefaults(
    defineProps<{
      value: string | string[];
      format?: string;
      localFormat?: string;
      valueClass?: string;
      type?: "date" | "time" | "datetime" | "schedule" | "times" | "dates";
      timeFormat?: string;
      isReverse?: boolean;
    }>(),
    {
      format: "",
      localFormat: "",
      valueClass: "",
      type: "datetime",
      timeFormat: "HH:mm",
      isReverse: false,
    }
  );

  const moscowOffset = 180;

  const isDisabled = computed(() => {
    return dayjs().utcOffset() === moscowOffset;
  });

  function formatRange(values: string[], format: string, offset: number) {
    if (values.length < 2) return dayjs(values[0]).add(offset, "minutes").format(format);
    const [startDate, endDate, startTime, endTime] = values;
    const start = dayjs(`${startDate} ${startTime}`).add(offset, "minutes");
    const end = dayjs(`${endDate} ${endTime}`).add(offset, "minutes");

    const startDateStr = start.format("DD.MM");
    const endDateStr = end.format("DD.MM");
    const startTimeStr = start.format("HH:mm");
    const endTimeStr = end.format("HH:mm");

    // Если даты одинаковые, время разные
    if (props.type === "dates") {
      return `${startDateStr} - ${endDateStr}`;
    }
    if (props.type === "times") {
      return `${startTimeStr} - ${endTimeStr}`;
    }
    // Если даты одинаковые, время разные
    if (startDateStr === endDateStr && startTimeStr !== endTimeStr) {
      return `${startDateStr} ${startTimeStr} - ${endTimeStr}`;
    }
    // Если время одинаковое, даты разные
    if (startDateStr !== endDateStr && startTimeStr === endTimeStr) {
      return `${startDateStr} - ${endDateStr} ${startTimeStr}`;
    }
    // Если время одинаковое, даты одинаковые
    if (startDateStr === endDateStr && startTimeStr === endTimeStr) {
      return `${startDateStr} ${startTimeStr}`;
    }
    // Если всё разное
    return `${startDateStr} - ${endDateStr} ${startTimeStr} - ${endTimeStr}`;
  }

  const formattedDate = computed(() => {
    if (Array.isArray(props.value)) {
      // Для диапазона дат
      return props.type === "schedule"
        ? formatRange(props.value, props.format || handleFormat(), moscowOffset)
        : formatRange(props.value, props.format || handleFormat(), moscowOffset);
    } else {
      // Формат для отображения по умолчанию
      if (props.type === "schedule") {
        return dayjs(props.value).format(props.format || handleFormat());
      }
      // Для времени парсим как полную дату, а не только время
      return dayjs(props.value)
        .add(props.isReverse ? moscowOffset - dayjs().utcOffset() : moscowOffset, "minutes")
        .format(props.format || handleFormat());
    }
  });

  const localDate = computed(() => {
    if (Array.isArray(props.value)) {
      return props.type === "schedule"
        ? formatRange(props.value, props.localFormat || handleFormat(), dayjs().utcOffset())
        : formatRange(props.value, props.localFormat || handleFormat(), dayjs().utcOffset());
    } else {
      // Формат для локального времени
      if (props.type === "schedule") {
        return dayjs(props.value).format(props.localFormat || handleFormat());
      }
      // Для времени парсим как полную дату, а не только время
      return dayjs(props.value)
        .add(props.isReverse ? 0 : dayjs().utcOffset(), "minutes")
        .format(props.localFormat || handleFormat());
    }
  });

  const handleFormat = () => {
    switch (props.type) {
      case "date":
        return "DD.MM.YYYY";
      case "time":
        return props.timeFormat;
      case "datetime":
        return "DD.MM.YYYY HH:mm";
      default:
        return "DD.MM.YYYY HH:mm";
    }
  };
</script>
